import { useInView } from '../hooks/useInView';
import { useCountUp } from '../hooks/useCountUp';

function BigNum({ value, suffix='%', active, color='var(--black)' }) {
  const v = useCountUp(value, 1100, active, 0);
  return (
    <span style={{ fontFamily:'var(--font-head)', fontSize:'clamp(3rem,6vw,5rem)', fontWeight:700, color, lineHeight:1, letterSpacing:'-0.04em' }}>
      {Math.round(v)}{suffix}
    </span>
  );
}

export default function TeamSection() {
  const [ref, inView] = useInView({ threshold:0.1 });
  return (
    <section id="team" className="section">
      <div className="container" ref={ref}>
        <div className={`reveal ${inView?'visible':''}`} style={{ marginBottom:'3.5rem' }}>
          <p className="eyebrow">Team Dynamics</p>
          <h2 className="h2">How they worked<br />together</h2>
          <p className="body-lg" style={{ maxWidth:'500px', marginTop:'0.75rem' }}>
            Students were asked to rate their team experience on a 5-point agreement scale. Here's what the data shows about collaboration inside JI 101.
          </p>
        </div>

        {/* 3 big stat blocks */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:'1.25rem', marginBottom:'1.5rem' }}>
          {[
            { value:81, label:'Felt heard', desc:'said their ideas were heard and considered by their team', color:'var(--accent)', bg:'var(--black)' },
            { value:85, label:'Diversity respected', desc:'felt their team respected and valued diverse perspectives', color:'var(--black)', bg:'var(--accent)' },
            { value:87, label:'Chose teamwork', desc:'preferred working as a team over working individually', color:'var(--accent)', bg:'var(--black)' },
          ].map((s, i) => (
            <div key={i} className={`reveal nb-card-dark ${inView?'visible':''}`} style={{ padding:'2.5rem 2rem', transitionDelay:`${i*0.1}s`, background: i===1?'var(--accent)':'var(--black)', boxShadow: i===1?'var(--shadow)':'5px 5px 0px var(--accent)' }}>
              <BigNum value={s.value} active={inView} color={i===1?'var(--black)':'var(--accent)'} />
              <div style={{ fontFamily:'var(--font-head)', fontSize:'0.8rem', fontWeight:700, color: i===1?'var(--black)':'var(--white)', marginTop:'0.6rem', marginBottom:'0.4rem', textTransform:'uppercase', letterSpacing:'0.05em' }}>{s.label}</div>
              <div style={{ fontFamily:'var(--font-body)', fontSize:'0.8rem', color: i===1?'rgba(0,0,0,0.6)':'rgba(255,255,255,0.55)', lineHeight:1.55 }}>{s.desc}</div>
            </div>
          ))}
        </div>

        {/* Two detail cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:'1.25rem' }}>
          <div className={`reveal nb-card ${inView?'visible':''}`} style={{ padding:'2rem', transitionDelay:'0.35s' }}>
            <div style={{ fontFamily:'var(--font-head)', fontSize:'0.62rem', fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'1rem' }}>Value violations</div>
            <div style={{ display:'flex', alignItems:'baseline', gap:'0.75rem', marginBottom:'0.5rem' }}>
              <span style={{ fontFamily:'var(--font-head)', fontSize:'2.5rem', fontWeight:700, color:'var(--accent)' }}>91%</span>
              <span style={{ fontFamily:'var(--font-head)', fontSize:'0.8rem', fontWeight:600 }}>No values violated</span>
            </div>
            <p className="body-sm">49 of 54 teams reported zero value violations throughout the workshop. Only 4 teams had one violation, 1 had two. A strong indicator of psychological safety and mutual respect.</p>
          </div>

          <div className={`reveal nb-card ${inView?'visible':''}`} style={{ padding:'2rem', transitionDelay:'0.42s' }}>
            <div style={{ fontFamily:'var(--font-head)', fontSize:'0.62rem', fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'1rem' }}>Visioning exercise</div>
            <div style={{ display:'flex', alignItems:'baseline', gap:'0.75rem', marginBottom:'0.5rem' }}>
              <span style={{ fontFamily:'var(--font-head)', fontSize:'2.5rem', fontWeight:700, color:'var(--accent)' }}>53/54</span>
              <span style={{ fontFamily:'var(--font-head)', fontSize:'0.8rem', fontWeight:600 }}>Teams said it helped</span>
            </div>
            <p className="body-sm">The visioning exercise — where teams defined their goal before identifying a problem — was near-universally effective at aligning teams before they began working. 98% of students said it helped create their team charter.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
